// src/dev-error-bridge.tsx
import React from 'react';

// --------------------
// Runtime error logging
// --------------------
if (import.meta.env.DEV) {
  const sendToTerminal = (type: 'error' | 'warn', ...args: unknown[]) => {
    // @ts-ignore
    if (window.__vite_hmr_ws__?.send) {
      window.__vite_hmr_ws__.send(JSON.stringify({ type: 'custom', event: type, data: args }));
    }
  };

  const originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    if (args.length > 0) {
      originalConsoleError('❌ [DEV ERROR]', ...args);
    } else {
      originalConsoleError('❌ [DEV ERROR]');
    }
    sendToTerminal('error', ...args);
  };

  const originalConsoleWarn = console.warn;
  console.warn = (...args: unknown[]) => {
    if (args.length > 0) {
      originalConsoleWarn('⚠️ [DEV WARN]', ...args);
    } else {
      originalConsoleWarn('⚠️ [DEV WARN]');
    }
    sendToTerminal('warn', ...args);
  };

  window.onerror = (message, source, lineno, colno, error) => {
    originalConsoleError(
      `❌ [DEV ERROR] ${message} at ${source}:${lineno}:${colno}`,
      error
    );
    sendToTerminal('error', message, source, lineno, colno, error);
    return false; // let Vite overlay still show
  };

  window.addEventListener('unhandledrejection', (event) => {
    originalConsoleError('❌ [DEV ERROR] Unhandled Promise Rejection:', event.reason);
    sendToTerminal('error', event.reason);
  });
}

// --------------------
// React component error boundary
// --------------------
export class DevErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  state = { hasError: false, error: undefined };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('❌ [DEV ERROR][React Component]', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ color: 'red', padding: '1rem' }}>
          <strong>Something went wrong (check console)</strong>
          <pre>
            {this.state.error
              ? (this.state.error as any)?.message ?? String(this.state.error)
              : 'Unknown error'}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}